import i18n from 'i18n';
import moment from 'moment';
import { converters as tableConverters } from 'store/table';
import {
  IListBenefit,
  IBenefitCategoryContent,
  IBenefitShopItemsConfig,
  IEmployeeBenefit,
  IBenefit,
  IBenefitContent,
} from 'types/income';
import { resolveTranslation } from 'utils/general';
import { convertUploadedFile } from 'store/files';
import { loaders } from './benefits.loaders';
import {
  BaseBenefit,
  ListBenefit,
  benefitCategories,
  BenefitCategoryContent,
  BenefitShopItemsConfig,
  Benefit,
  BenefitContent,
} from './benefits.types';

const convertBenefit = (benefit: IBenefit): Benefit => {
  return {
    id: benefit.id,
    name: benefit.name,
    value: benefit.value ?? NaN,
    type: benefit.type,
    status: benefit.isDraft
      ? 'draft'
      : benefit.activatedAt && moment().isBefore(benefit.activatedAt)
      ? 'inactive'
      : benefit.deactivatedAt && moment().isSameOrAfter(benefit.deactivatedAt)
      ? 'expired'
      : 'active',
    publishStatus:
      benefit.isDraft ||
      (benefit.publishedAt && moment().isBefore(benefit.publishedAt)) ||
      (benefit.deactivatedAt && moment().isSameOrAfter(benefit.deactivatedAt))
        ? 'unpublished'
        : 'published',
    investmentAmount: benefit.investmentAmount ?? NaN,
    employeeCount: benefit.employeeCount,
    activatedAt: benefit.activatedAt,
    deactivatedAt: benefit.deactivatedAt,
    allowClaimReimbursement: benefit.allowClaimReimbursement,
    investmentPeriod: benefit.investmentPeriod,
    employeeContribution: benefit.employeeContribution ?? NaN,
    isVisibleForEmployees: benefit.isVisibleForEmployees,
    isAccumulating: benefit.isAccumulating,
    isBalanceTrackingEnabled: benefit.isBalanceTrackingEnabled,
    internalComment: benefit.internalComment,
    isPreview: benefit.isPreview,
    previewDate: benefit.previewDate,
    publishedAt: benefit.publishedAt,
    groupCount: benefit.groupCount,
    isArchived: benefit.isArchived,
    allocationPriority: benefit.allocationPriority,
    taxExemptionCountry: benefit.taxExemptionCountry ?? null,
  };
};

const convertListBenefit = (benefit: IListBenefit): ListBenefit => {
  return {
    id: benefit.id,
    name: benefit.name,
    value: benefit.value === null ? NaN : Number(benefit.value) * 100,
    type: benefit.type,
    status: benefit.status,
    publishStatus: benefit.publishStatus,
    investmentAmount:
      benefit.investmentAmount === null
        ? NaN
        : Number(benefit.investmentAmount) * 100,
    employeeCount: benefit.employeeCount,
    activatedAt: benefit.activationDate,
    deactivatedAt: benefit.deactivationDate,
    allowClaimReimbursement: benefit.allowClaimReimbursement,
    trackBalance: benefit.trackBalance,
    isArchived: benefit.isArchived,
  };
};

const convertBenefits = (benefits: IListBenefit[]): ListBenefit[] => {
  return benefits.map(convertListBenefit);
};

const convertBenefitContent = (benefit: IBenefitContent): BenefitContent => {
  return {
    translations: benefit.translations.map((trans) => ({
      ...trans,
      title: trans.title,
      description: trans.description ?? '',
      image: trans.image?.id ? convertUploadedFile(trans.image) : null,
      links:
        trans.links?.map((link) => ({
          ...link,
          name: link.name,
          url: link.url,
        })) ?? [],
      faqs:
        trans.faqs?.map((faq) => ({
          ...faq,
          question: faq.question,
          answer: faq.answer,
        })) ?? [],
      attachments:
        trans.attachments?.map((attachment) => ({
          ...attachment,
          name: attachment.name,
          file: convertUploadedFile(attachment.file),
        })) ?? [],
    })),
  };
};

const convertEmployeeBenefits = (
  benefits: IEmployeeBenefit[],
): BaseBenefit[] => {
  return benefits.map((benefit) => ({
    id: benefit.benefitId,
    name: benefit.benefitName,
  }));
};

const convertCategoriesContent = (
  categories: IBenefitCategoryContent[],
): BenefitCategoryContent[] => {
  return benefitCategories.map((category) => {
    const translations =
      categories.find((c) => c.enum === category)?.translations ?? [];
    const label =
      resolveTranslation(translations)?.label ?? i18n.t(`menu.${category}`);

    return {
      id: category,
      category,
      label,
      translations,
    };
  });
};

const convertShopItemsConfig = ({
  assignmentFilter: config,
}: IBenefitShopItemsConfig): BenefitShopItemsConfig => {
  if (!config) return config;
  return {
    price:
      config.priceFrom && config.priceTo
        ? {
            from: config.priceFrom,
            to: config.priceTo,
          }
        : null,
    items: config.itemIds ?? [],
    categories: config.categories ?? [],
    benefitCategories: config.benefitPlanTypes ?? [],
    countries: config.countries ?? [],
    cities: config.cities ?? [],
    suppliers: {
      value: config.supplierIds ?? [],
      isInverted: config.invertedFilters?.includes('supplierIds') ?? false,
    },
    taxExemptionStatus: config.taxExemptionStatus ?? [],
    taxExemptionCountries: config.taxExemptionCountries ?? [],
  };
};

export const converters = {
  getBenefits: ({ data }: Awaited<ReturnType<typeof loaders.getBenefits>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertBenefits(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getBenefit: ({ data }: Awaited<ReturnType<typeof loaders.getBenefit>>) => {
    return convertBenefit(data);
  },
  getBenefitContent: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getBenefitContent>>) => {
    return convertBenefitContent(data);
  },
  getEmployeeBenefits: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getEmployeeBenefits>>) => {
    return convertEmployeeBenefits(data.data);
  },
  getCategoriesContent: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getCategoriesContent>>) => {
    const items = convertCategoriesContent(data.data);

    return {
      items,
      total: items.length,
    };
  },
  getShopItemsConfig: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getShopItemsConfig>>) => {
    return convertShopItemsConfig(data);
  },
};
